import React, { useState } from "react";
import { Avatar, Button, Divider, Popover, Select, theme, Typography } from "antd";
import type { MenuProps } from "antd";
import { LockOutlined, PoweroffOutlined, SettingOutlined } from "@ant-design/icons";
import { FaMoon, FaSun } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import "./user-panel.scss";

const { Text } = Typography;

interface UserPanelProps {
    isMenuCollapsed: boolean;
    userName: string;
    userEmail: string;
    isLightTheme: boolean;
    lang: string;
    languages: MenuProps['items'];
    contentStyle: {
        colorPrimary: string;
        colorError: string;
        colorYellow: string;
        colorBlue: string;
    };
    onAction: (key: string) => void;
    onLanguageChange: (key: string) => void;
    greeting: () => React.ReactNode;
}

const UserPanel = ({ isMenuCollapsed, userName, userEmail, isLightTheme, lang, languages, contentStyle, onAction, onLanguageChange }: UserPanelProps) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [expanded, setExpanded] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);

    const initials = userName
        ? userName.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')
        : '?';

    const avatarStyle: React.CSSProperties = {
        background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryHover} 100%)`,
        color: '#fff',
        fontWeight: 600,
        fontSize: '16px',
        letterSpacing: '0.5px',
        boxShadow: `0 4px 12px ${token.colorPrimary}20, 0 0 0 3px ${token.colorBorder}`,
        border: 'none',
        flexShrink: 0,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
    };

    const actionButtons = (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                    icon={<PoweroffOutlined style={{ color: contentStyle.colorError }} />}
                    onClick={() => onAction('3')}
                    style={{ width: 52, height: 52, borderRadius: 12 }}
                />
                <Text style={{ fontSize: 11, textAlign: 'center', maxWidth: 56 }} ellipsis>{t("auth.logout")}</Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                    icon={<SettingOutlined style={{ color: contentStyle.colorBlue }} />}
                    onClick={() => onAction('1')}
                    style={{ width: 52, height: 52, borderRadius: 12 }}
                />
                <Text style={{ fontSize: 11, textAlign: 'center', maxWidth: 56 }} ellipsis>{t("auth.user_settings")}</Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                    icon={<LockOutlined style={{ color: contentStyle.colorPrimary }} />}
                    onClick={() => onAction('2')}
                    style={{ width: 52, height: 52, borderRadius: 12 }}
                />
                <Text style={{ fontSize: 11, textAlign: 'center', maxWidth: 56 }} ellipsis>{t("auth.change_password")}</Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Button
                    icon={isLightTheme ? <FaMoon style={{ color: contentStyle.colorBlue }} /> : <FaSun style={{ color: contentStyle.colorYellow }} />}
                    onClick={() => onAction('4')}
                    style={{ width: 52, height: 52, borderRadius: 12 }}
                />
                <Text style={{ fontSize: 11, textAlign: 'center', maxWidth: 56 }} ellipsis>{isLightTheme ? t("auth.dark") : t("auth.light")}</Text>
            </div>
        </div>
    );

    if (isMenuCollapsed) {
        const popoverContent = (
            <div className="user-panel-popover-content">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0 8px' }}>
                    <Avatar className="user-panel-avatar" style={avatarStyle} size={48}>{initials}</Avatar>
                    <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                        <Text strong ellipsis style={{ display: 'block' }}>{userName}</Text>
                        <Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>{userEmail}</Text>
                    </div>
                    <Button
                        type="text"
                        icon={<SettingOutlined style={{ color: contentStyle.colorBlue }} />}
                        onClick={() => { onAction('1'); setPopoverOpen(false); }}
                    />
                </div>
                <Divider style={{ margin: '8px 0' }} />
                {actionButtons}
                <Divider style={{ margin: '8px 0' }} />
                <Select
                    size="middle"
                    style={{ width: '100%' }}
                    defaultValue={lang}
                    showSearch
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input?.toLowerCase())}
                    options={languages ? languages.map((l: any) => ({ value: l.key, label: l.label })) : []}
                    onChange={onLanguageChange}
                />
            </div>
        );

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', pointerEvents: 'auto' }}>
                <div style={{ flex: 1 }} />
                <Popover
                    content={popoverContent}
                    trigger="click"
                    placement="rightBottom"
                    arrow={false}
                    open={popoverOpen}
                    onOpenChange={setPopoverOpen}
                >
                    <div className="user-panel-trigger-collapsed">
                        <Avatar className="user-panel-avatar" style={avatarStyle} size={40}>{initials}</Avatar>
                    </div>
                </Popover>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', pointerEvents: 'auto' }}>
            <div style={{ flex: 1 }} />
            <div className={`user-panel-actions${expanded ? ' user-panel-actions--open' : ''}`}>
                <Divider style={{ margin: '4px 0' }} />
                {actionButtons}
            </div>

            <div className="user-panel-user-row">
                <Avatar className="user-panel-avatar" style={avatarStyle} size={40}>{initials}</Avatar>
                <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                    <Text strong ellipsis style={{ display: 'block' }}>{userName}</Text>
                    <Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>{userEmail}</Text>
                </div>
                <Button
                    type="text"
                    className={`user-panel-gear-btn${expanded ? ' is-open' : ''}`}
                    icon={<SettingOutlined style={{ color: contentStyle.colorBlue }} />}
                    onClick={() => setExpanded(prev => !prev)}
                />
            </div>

            <div className="user-panel-language">
                <Select
                    size="middle"
                    style={{ width: '100%' }}
                    defaultValue={lang}
                    showSearch
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input?.toLowerCase())}
                    options={languages ? languages.map((l: any) => ({ value: l.key, label: l.label })) : []}
                    onChange={onLanguageChange}
                />
            </div>
        </div>
    );
};

export default UserPanel;
